import { useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { useGetChangeStatus } from '../contexts/CandidateService'
import {useGetNextStatusChange, useSendChangeStatus} from '../contexts/CandidateService/candidateServiceHooks'

export interface CandidateWorkflowState {
    EId: string
    kind: string
    name: string
    predefinedKind: null | "SUBMITTED_TO_HIRING_MANAGER" | string
    title: string
}

export interface GetChangeStatusStatesResponse {
    candidateWorkFlowStates: Array<CandidateWorkflowState>
}

interface GetChangeStatusStatesProps {
    reqId: string
    currentStatus?: string
}
export function useGetChangeStatusStates(props: GetChangeStatusStatesProps) {
    const isMounted = useRef(false)
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<GetChangeStatusStatesResponse>()
    const [states, setStates] = useState<Array<CandidateWorkflowState>>([])
    const [error, setError] = useState('')

    const getChangeStatusStates = props.currentStatus ? useGetNextStatusChange() : useGetChangeStatus()

    useEffect(() => {
        isMounted.current = true

        if (props.reqId) {
            setLoading(true)
            setError('')
            getChangeStatusStates(props, (response) => {
                if (!isMounted) return

                setLoading(false)

                if (response.status === 200) {
                    setData(response.data)
                    setStates(response.data.candidateWorkFlowStates)
                } else {
                    setError('An unknown error occurred')
                }
            })
        }

        return () => {
            isMounted.current = false
        }
    }, [props])

    return useMemo(() => ({
        loading,
        error,
        states,
    }), [
        loading,
        error,
        states,
        data,
    ])
}

export interface ChangeStatusResponse {
    msgInfo: {
        message: string
        statusCode: number
    }
}

export interface ChangeStatusPayload {
    appIds: Array<string>
    workflowStateName: string
}

interface UseChangeStatusReqProps {
    appIds: Array<string>
    workflowStateName: string
}
export function useChangeStatusReq(props: UseChangeStatusReqProps) {
    const isMounted = useRef(false)
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<ChangeStatusResponse>()
    const [error, setError] = useState('')

    const sendChangeStatus = useSendChangeStatus()

    const changeStatus = useCallback(() => {
        return new Promise(function(resolve, reject) {
            try {
                setLoading(true)
                setError('')

                sendChangeStatus(props, (response) => {
                    if (!isMounted) return

                    setLoading(false)

                    if (response.data && response.data.msgInfo && response.data.msgInfo.statusCode && response.data.msgInfo.statusCode === 200) {
                        setData(response.data)
                        resolve(response)
                    } else {
                        setError('An unknown error occurred')
                        reject(response)
                    }
                })
            } catch(err) {
                setError('An unknown error occurred')
                reject(err)
            }
        })
    }, [props, props.appIds, props.workflowStateName])

    useEffect(() => {
        isMounted.current = true

        return () => {
            isMounted.current = false
        }
    }, [])

    return useMemo(() => ({
        loading,
        error,
        changeStatus,
    }), [
        loading,
        error,
        changeStatus,
    ])
}
